import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronDown } from "lucide-react";
import { User, displayName, initials, avatarColor, loadUsers } from "./user-display";

interface Props {
    value: string[];
    onChange: (ids: string[]) => void;
    placeholder?: string;
    excludeIds?: string[];
    // when true, read-only users are hidden (e.g. choosing a page owner, who must
    // be able to manage the page)
    excludeReadonly?: boolean;
}

export default function UserPicker({ value, onChange, placeholder = "Type to add...", excludeIds = [], excludeReadonly = false }: Props) {
    const [users, setUsers] = useState<User[]>([]);
    const [query, setQuery] = useState("");
    const [open, setOpen] = useState(false);
    const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);
    const wrapRef = useRef<HTMLDivElement>(null);
    const menuRef = useRef<HTMLDivElement>(null);

    useEffect(() => { loadUsers().then(setUsers).catch(() => {}); }, []);

    useLayoutEffect(() => {
        if (!open || !wrapRef.current) return;
        const update = () => {
            if (!wrapRef.current) return;
            const r = wrapRef.current.getBoundingClientRect();
            setMenuPos({ top: r.bottom + 4, left: r.left, width: r.width });
        };
        update();
        window.addEventListener("scroll", update, true);
        window.addEventListener("resize", update);
        return () => {
            window.removeEventListener("scroll", update, true);
            window.removeEventListener("resize", update);
        };
    }, [open]);

    useEffect(() => {
        if (!open) return;
        const onClick = (e: MouseEvent) => {
            const t = e.target as Node;
            if (wrapRef.current?.contains(t)) return;
            if (menuRef.current?.contains(t)) return;
            setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, [open]);

    const selected = users.filter(u => value.includes(u.id));
    const excludeSet = new Set([...value, ...excludeIds]);
    const q = query.trim().toLowerCase();
    const matches = users
        .filter(u => !excludeSet.has(u.id))
        .filter(u => !excludeReadonly || u.manualsRole !== "readonly")
        .filter(u => !q
            || displayName(u).toLowerCase().includes(q)
            || u.name?.toLowerCase().includes(q)
            || u.email?.toLowerCase().includes(q))
        // the menu is scrollable; cap high just to bound rendering as the org grows
        .slice(0, 100);

    const add = (id: string) => {
        onChange([...value, id]);
        setQuery("");
    };
    const remove = (id: string) => onChange(value.filter(v => v !== id));

    return (
        <div className="m-userpicker" ref={wrapRef}>
            <div className="m-userpicker-box" onClick={() => setOpen(true)}>
                {selected.map(u => {
                    const name = displayName(u);
                    return (
                        <span key={u.id} className="m-userpicker-chip">
                            <span className="m-userpicker-avatar sm" style={{ background: avatarColor(u.id) }}>{initials(name)}</span>
                            {name}
                            <button type="button" className="m-userpicker-chip-x" onClick={e => { e.stopPropagation(); remove(u.id); }}>
                                <X size={11} />
                            </button>
                        </span>
                    );
                })}
                <input
                    type="text"
                    className="m-userpicker-input"
                    value={query}
                    onChange={e => { setQuery(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    placeholder={selected.length === 0 ? placeholder : ""}
                />
                <button type="button" className="m-userpicker-caret-btn" onClick={e => { e.stopPropagation(); setOpen(o => !o); }}>
                    <ChevronDown size={14} />
                </button>
            </div>
            {open && matches.length > 0 && menuPos && createPortal(
                <div
                    ref={menuRef}
                    className="m-userpicker-menu"
                    style={{ position: "fixed", top: menuPos.top, left: menuPos.left, width: menuPos.width, zIndex: 1080 }}
                >
                    {matches.map(u => {
                        const name = displayName(u);
                        return (
                            <button type="button" key={u.id} className="m-userpicker-option" onMouseDown={e => e.preventDefault()} onClick={() => add(u.id)}>
                                <span className="m-userpicker-avatar" style={{ background: avatarColor(u.id) }}>{initials(name)}</span>
                                <span className="m-userpicker-option-name">{name}</span>
                            </button>
                        );
                    })}
                </div>,
                document.body
            )}
        </div>
    );
}
